import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Transition } from '@uirouter/core';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { PlansService } from 'src/app/components/plans/plans.service';
import { PricingService } from '../../services/pricing.service';
import { PurchaseAddOnsService } from '../../services/purchase-add-ons.service';
import { AiCreditsEstimate } from '../../services/purchase-ai-credits.service';
import { InteractiveLicenseEstimate } from '../../services/purchase-interactive.service';

@Component({
  selector: 'select-plan',
  templateUrl: './select-plan.component.html',
  styleUrls: ['./select-plan.component.scss'],
  standalone: false,
})
export class SelectPlanComponent implements OnInit {
  private totalPriceDependencies = signal(0);
  private _isUnlimitedPlan = false;
  get isUnlimitedPlan(): boolean {
    return this._isUnlimitedPlan;
  }
  @Input() set isUnlimitedPlan(value: boolean) {
    this._isUnlimitedPlan = value;
    this.bumpTotalPriceDependencies();
  }
  private _periodMonthly = false;
  get periodMonthly(): boolean {
    return this._periodMonthly;
  }
  @Input() set periodMonthly(value: boolean) {
    const changedToMonthly = value && !this._periodMonthly;
    this._periodMonthly = value;
    this.bumpTotalPriceDependencies();

    if (changedToMonthly) {
      this.onInteractiveQuantityChange(0);
      this.onAiCreditsQuantityChange(0);
    }
  }
  _displayCount: number = 5;
  get displayCount(): number {
    return this._displayCount;
  }
  @Input() set displayCount(value: number) {
    this._displayCount = Math.floor(value);
    this.bumpTotalPriceDependencies();
    this.displayCountChange.emit(this._displayCount);
  }
  _unlimitedCount: number = 1;
  get unlimitedCount(): number {
    return this._unlimitedCount;
  }
  @Input() set unlimitedCount(value: number) {
    this._unlimitedCount = Math.floor(value);
    this.bumpTotalPriceDependencies();
    this.unlimitedCountChange.emit(this._unlimitedCount);
  }
  @Input() planType?: string;
  @Input() subscriptionId?: string;
  @Output() isUnlimitedPlanChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() isVolumePlan: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() periodMonthlyChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() selectedProductCodeChange: EventEmitter<string> =
    new EventEmitter<string>();
  @Output() totalPriceChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() displayCountChange: EventEmitter<number> =
    new EventEmitter<number>();
  @Output() unlimitedCountChange: EventEmitter<number> =
    new EventEmitter<number>();

  applyDiscount = this.companyStateService.isDiscountCustomer();
  canAccessUnlimitedPlan = this.companyStateService.isK12Customer();
  private _isBasicPlan = false;
  get isBasicPlan(): boolean {
    return this._isBasicPlan;
  }
  set isBasicPlan(value: boolean) {
    this._isBasicPlan = value;
    this.bumpTotalPriceDependencies();
  }
  private _isAdvancedPlan = true;
  get isAdvancedPlan(): boolean {
    return this._isAdvancedPlan;
  }
  set isAdvancedPlan(value: boolean) {
    this._isAdvancedPlan = value;
    this.bumpTotalPriceDependencies();
  }
  private _isEnterprisePlan = false;
  get isEnterprisePlan(): boolean {
    return this._isEnterprisePlan;
  }
  set isEnterprisePlan(value: boolean) {
    this._isEnterprisePlan = value;
    this.bumpTotalPriceDependencies();
  }
  allowMultiple = false;
  interactiveLicensesCount = 0;
  interactiveEstimate = signal<InteractiveLicenseEstimate | null>(null);
  interactiveEstimateError = signal(false);
  interactiveEstimateLoading = signal(false);
  aiCreditsPerMonth = signal(0);
  aiCreditsEstimate = signal<AiCreditsEstimate | null>(null);
  aiCreditsEstimateError = signal(false);
  aiCreditsEstimateLoading = signal(false);
  hasAiCreditsPerMonth = computed(() => this.aiCreditsPerMonth() > 0);
  interactiveEstimateAmount = computed(
    () => this.interactiveEstimate()?.amountDueNow ?? null,
  );
  interactiveEstimateCurrencyCode = computed(
    () => this.interactiveEstimate()?.currencyCode?.toUpperCase() ?? 'USD',
  );
  aiCreditsEstimateAmount = computed(() => {
    const aiCreditsEstimate = this.aiCreditsEstimate();

    if (!aiCreditsEstimate) {
      return null;
    }

    // total is in cents; amountDueNow is already in dollars.
    return aiCreditsEstimate.total !== undefined
      ? aiCreditsEstimate.total / 100
      : aiCreditsEstimate.amountDueNow;
  });
  aiCreditsEstimateCurrencyCode = computed(
    () => this.aiCreditsEstimate()?.currencyCode?.toUpperCase() ?? 'USD',
  );
  totalPrice = computed(() => {
    this.totalPriceDependencies();

    let total = 0;
    if (this.isUnlimitedPlan) {
      total += this.unlimitedTotal;
    }
    if (this.isPerDisplayPlan) {
      total += this.displayTotal;
    }

    const interactiveEstimateAmount = this.interactiveEstimateAmount();
    if (
      this.hasInteractiveLicenses &&
      !this.interactiveEstimateError() &&
      interactiveEstimateAmount !== null
    ) {
      total += interactiveEstimateAmount;
    }

    const aiCreditsEstimateAmount = this.aiCreditsEstimateAmount();
    if (
      this.hasAiCreditsPerMonth() &&
      !this.aiCreditsEstimateError() &&
      aiCreditsEstimateAmount !== null
    ) {
      total += aiCreditsEstimateAmount;
    }

    return total;
  });

  private interactiveEstimateRequestId = 0;
  private aiCreditsEstimateRequestId = 0;

  constructor(
    private plansService: PlansService,
    private pricingService: PricingService,
    private companyStateService: CompanyStateService,
    private transition: Transition,
    private destroyRef: DestroyRef,
    private purchaseAddOnsService: PurchaseAddOnsService,
  ) {}

  ngOnInit(): void {
    const enterprise = Number(this.transition.params().enterprise);
    const unlimited = Number(this.transition.params().unlimited);

    if (!isNaN(enterprise) || !isNaN(unlimited)) {
      this.allowMultiple = this.companyStateService.isK12Customer();
      this.isAdvancedPlan = false;
      this.isUnlimitedPlan = !isNaN(unlimited) && unlimited > 0;
      if (!isNaN(enterprise) && enterprise > 0) {
        this.displayCount = enterprise;
        this.isEnterprisePlan = true;
      }
      if (this.isUnlimitedPlan) {
        this.unlimitedCount = unlimited;
      }

      // No plan was selected, use defaults
      if (!this.isUnlimitedPlan && !this.isEnterprisePlan) {
        if (this.companyStateService.isK12Customer()) {
          this.isUnlimitedPlan = true;
        } else {
          this.isEnterprisePlan = true;
        }
      }
    } else if (this.isUnlimitedPlan) {
      this.isAdvancedPlan = false;
    } else if (this.planType === 'advanced') {
      this.isAdvancedPlan = false;
      this.isEnterprisePlan = true;
    }
    this._changePlan();
  }

  get isPerDisplayPlan(): boolean {
    return this.isBasicPlan || this.isAdvancedPlan || this.isEnterprisePlan;
  }

  get selectedProductCode(): string | undefined {
    const plan = this.isBasicPlan
      ? this.plansService.getBasicPlan(this.applyDiscount)
      : this.isAdvancedPlan
        ? this.plansService.getAdvancedPlan(this.applyDiscount)
        : this.isEnterprisePlan
          ? this.plansService.getEnterprisePlan(this.applyDiscount)
          : this.isUnlimitedPlan
            ? this.plansService.getUnlimitedPlan()
            : undefined;
    const productCode = plan ? plan.productCode : undefined;
    return productCode;
  }

  get hasInteractiveLicenses(): boolean {
    return this.interactiveLicensesCount > 0;
  }

  get unlimitedPrice(): number {
    return this.pricingService.getTierPrice(
      false,
      this.unlimitedCount,
      this.plansService.getUnlimitedPlan().productCode,
    );
  }

  get unlimitedTotal(): number {
    return this.unlimitedPrice * this.unlimitedCount;
  }

  get displayTotal(): number {
    return this.pricingService.getTotalPrice(
      this.periodMonthly && this.isBasicPlan,
      this.displayCount,
      this.applyDiscount,
      this.selectedProductCode,
    );
  }

  get unlimitedMonthlyPrice(): number {
    return this.plansService.getUnlimitedPlan().yearly.billAmount / 12;
  }

  get baseDisplayPrice(): number {
    if (this.isBasicPlan) {
      const monthlyPrice = this.pricingService.getBasePricePerDisplay(
        1,
        this.selectedProductCode,
      );
      return this.periodMonthly ? monthlyPrice : monthlyPrice * 12;
    }
    return this.pricingService.getPricePerDisplay(
      false,
      1,
      false,
      this.selectedProductCode,
    );
  }

  get pricePerDisplay(): number {
    return this.pricingService.getPricePerDisplay(
      this.periodMonthly && this.isBasicPlan,
      this.displayCount,
      this.applyDiscount,
      this.selectedProductCode,
    );
  }

  get basicPricePerDisplay(): number {
    return this.pricingService.getPricePerDisplay(
      this.periodMonthly,
      this.displayCount,
      this.applyDiscount,
      this.plansService.getBasicPlan(this.applyDiscount).productCode,
    );
  }

  get advancedPricePerDisplay(): number {
    return this.pricingService.getPricePerDisplay(
      false,
      this.displayCount,
      this.applyDiscount,
      this.plansService.getAdvancedPlan(this.applyDiscount).productCode,
    );
  }

  get enterprisePricePerDisplay(): number {
    return this.pricingService.getPricePerDisplay(
      false,
      this.displayCount,
      this.applyDiscount,
      this.plansService.getEnterprisePlan(this.applyDiscount).productCode,
    );
  }

  get yearlySavings(): number {
    if (this.isUnlimitedPlan && !this.isPerDisplayPlan) {
      return 0;
    }

    const isMonthlyBilling = this.periodMonthly && this.isBasicPlan;
    const baseTotal = this.baseDisplayPrice * this.displayCount;
    const annualBaseTotal = isMonthlyBilling ? baseTotal * 12 : baseTotal;
    const annualActualTotal = isMonthlyBilling
      ? this.displayTotal * 12
      : this.displayTotal;

    return annualBaseTotal - annualActualTotal;
  }

  private _changePlan() {
    this.bumpTotalPriceDependencies();
    this.selectedProductCodeChange.emit(this.selectedProductCode);
    this.isUnlimitedPlanChange.emit(this.isUnlimitedPlan);
    this.isVolumePlan.emit(
      this.isBasicPlan || this.isAdvancedPlan || this.isEnterprisePlan,
    );
    this.totalPriceChange.emit(this.totalPrice());
    this.periodMonthlyChange.emit(
      this.isBasicPlan ? this.periodMonthly : false,
    );
  }

  updatePeriodMonthly(value: boolean) {
    this.periodMonthly = value;
    this.periodMonthlyChange.emit(value);
  }

  onInteractiveQuantityChange(quantity: number) {
    this.interactiveLicensesCount = this.periodMonthly
      ? 0
      : Math.floor(Number(quantity) || 0);
    this.interactiveEstimate.set(null);
    this.interactiveEstimateError.set(false);

    const requestId = ++this.interactiveEstimateRequestId;
    this.interactiveEstimateLoading.set(this.hasInteractiveLicenses);
    this.totalPriceChange.emit(this.totalPrice());

    if (!this.hasInteractiveLicenses) {
      this.purchaseAddOnsService.clearInteractiveSelection();
      return;
    }

    this.purchaseAddOnsService
      .estimateInteractiveSubscription(this.interactiveLicensesCount)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (estimate) => {
          if (requestId === this.interactiveEstimateRequestId) {
            this.interactiveEstimate.set(estimate);
            this.interactiveEstimateLoading.set(false);
            this.totalPriceChange.emit(this.totalPrice());
          }
        },
        error: () => {
          if (requestId === this.interactiveEstimateRequestId) {
            this.interactiveEstimateError.set(true);
            this.interactiveEstimateLoading.set(false);
            this.totalPriceChange.emit(this.totalPrice());
          }
        },
      });
  }

  onAiCreditsQuantityChange(quantity: number) {
    const aiCreditsPerMonth = this.periodMonthly
      ? 0
      : Math.floor(Number(quantity) || 0);
    this.aiCreditsPerMonth.set(aiCreditsPerMonth);
    this.aiCreditsEstimate.set(null);
    this.aiCreditsEstimateError.set(false);

    const requestId = ++this.aiCreditsEstimateRequestId;
    this.aiCreditsEstimateLoading.set(this.hasAiCreditsPerMonth());
    this.totalPriceChange.emit(this.totalPrice());

    if (!this.hasAiCreditsPerMonth()) {
      this.purchaseAddOnsService.clearAICreditsSelection();
      return;
    }

    this.purchaseAddOnsService
      .estimateAICreditsSubscription(aiCreditsPerMonth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (estimate) => {
          if (requestId === this.aiCreditsEstimateRequestId) {
            this.aiCreditsEstimate.set(estimate);
            this.aiCreditsEstimateLoading.set(false);
            this.totalPriceChange.emit(this.totalPrice());
          }
        },
        error: () => {
          if (requestId === this.aiCreditsEstimateRequestId) {
            this.aiCreditsEstimateError.set(true);
            this.aiCreditsEstimateLoading.set(false);
            this.totalPriceChange.emit(this.totalPrice());
          }
        },
      });
  }

  private bumpTotalPriceDependencies() {
    this.totalPriceDependencies.update((value) => value + 1);
  }

  selectBasic() {
    if (!this.allowMultiple) {
      if (
        this.planType !== 'basic' &&
        (!this.isBasicPlan || this.isUnlimitedPlan)
      ) {
        this.isBasicPlan = true;
        this.isAdvancedPlan = false;
        this.isEnterprisePlan = false;
        this.isUnlimitedPlan = false;
      }
      this._changePlan();
    }
  }

  selectAdvanced() {
    if (!this.allowMultiple) {
      if (
        this.planType !== 'advanced' &&
        (!this.isAdvancedPlan || this.isUnlimitedPlan)
      ) {
        this.isBasicPlan = false;
        this.isAdvancedPlan = true;
        this.isEnterprisePlan = false;
        this.isUnlimitedPlan = false;
      }
      this._changePlan();
    }
  }

  selectEnterprise() {
    if (this.planType !== 'enterprise' && !this.isEnterprisePlan) {
      this.isBasicPlan = false;
      this.isAdvancedPlan = false;
      this.isEnterprisePlan = true;
      if (!this.allowMultiple) {
        this.isUnlimitedPlan = false;
      }
    } else if (this.isEnterprisePlan) {
      this.isEnterprisePlan = false;
      if (this.allowMultiple) {
        this.isUnlimitedPlan = true;
      }
    }
    this._changePlan();
  }

  selectUnlimited() {
    if (this.planType !== 'unlimited' && !this.isUnlimitedPlan) {
      this.isBasicPlan = false;
      this.isAdvancedPlan = false;
      if (!this.allowMultiple) {
        this.isEnterprisePlan = false;
      }
      this.isUnlimitedPlan = true;
    } else if (this.isUnlimitedPlan) {
      this.isUnlimitedPlan = false;
      if (this.allowMultiple) {
        this.isEnterprisePlan = true;
      }
    }
    this._changePlan();
  }

  selectEnterpriseOrUnlimited() {
    if (this.isBasicPlan || this.isAdvancedPlan) {
      this.canAccessUnlimitedPlan
        ? this.selectUnlimited()
        : this.selectEnterprise();
    }
  }
}
